/*
  Consent manager for Pizza On The Road.
  Compliant with Garante Privacy guidelines (10 giugno 2021).
  Stores consent in localStorage for 6 months.
  Blocks non-technical scripts (PostHog, Emergent platform script, Google Maps iframe)
  until explicit per-category consent.
*/

const STORAGE_KEY = "ptr_consent_v1";
const EXPIRY_DAYS = 180; // 6 months
const CONSENT_VERSION = 1;

// Category defaults
export const CATEGORIES = {
  necessary: { required: true, default: true },
  analytics: { required: false, default: false },
  thirdparty: { required: false, default: false },
};

export function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    // check expiry
    const now = Date.now();
    if (parsed.exp && now > parsed.exp) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(prefs) {
  const payload = {
    v: CONSENT_VERSION,
    ts: Date.now(),
    exp: Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    prefs: {
      necessary: true,
      analytics: !!prefs.analytics,
      thirdparty: !!prefs.thirdparty,
    },
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable - fail silently */
  }
  applyConsent(payload.prefs);
  // Notify listeners
  window.dispatchEvent(
    new CustomEvent("ptr-consent-change", { detail: payload.prefs })
  );
  return payload;
}

export function clearConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  // Reload to unload any already-loaded script
  window.location.reload();
}

export function getPrefs() {
  const c = loadConsent();
  if (!c) return null;
  return c.prefs;
}

// ============ Script loaders ============

let posthogLoaded = false;
let emergentLoaded = false;

function loadPostHog() {
  if (posthogLoaded) return;
  posthogLoaded = true;
  // PostHog loader snippet (official)
  /* eslint-disable */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
  /* eslint-enable */
}

function loadEmergentScript() {
  if (emergentLoaded) return;
  emergentLoaded = true;
  const s = document.createElement("script");
  s.src = "https://assets.emergent.sh/scripts/emergent-main.js";
  s.async = true;
  document.body.appendChild(s);
}

export function applyConsent(prefs) {
  if (!prefs) return;
  if (prefs.analytics) loadPostHog();
  if (prefs.thirdparty) loadEmergentScript();
}
